import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth, usePermissions } from './AuthContext';
import LoginPage from './LoginPage';
import DashboardLayout from './components/DashboardLayout';
import OfficeLayout from './components/OfficeLayout';
import DashboardHomePage from './pages/DashboardHomePage';
import AllLeadsPage from './pages/AllLeadsPage';
import FollowUpPage from './pages/FollowUpPage';
import SettingsPage from './pages/SettingsPage';
import BrokersPage from './pages/BrokersPage';
import ReportsPage from './pages/ReportsPage';
import JobApplicationsPage from './pages/JobApplicationsPageV2';
import OfficeSettingsPage from './pages/OfficeSettingsPage';
import OfficeUnitsPage from './pages/OfficeUnitsPage';
import OfficeUnitDetailPage from './pages/OfficeUnitDetailPage';
import OfficeBanksPage from './pages/OfficeBanksPage';
import OfficeBankReconciliationPage from './pages/OfficeBankReconciliationPage';
import OfficeReportsPage from './pages/OfficeReportsPage';
import OfficeTodayPage from './pages/OfficeTodayPage';
import OfficeUnitDetailClassicPage from './pages/OfficeUnitDetailClassicPage';
import OfficeChangeOrdersPage from './pages/OfficeChangeOrdersPage';
import OfficeIssuesPage from './pages/OfficeIssuesPage';
import OfficeEstimationPage from './pages/OfficeEstimationPage';
import OfficeFeasibilityPage from './pages/OfficeFeasibilityPage';
import OfficeContactBookPage from './pages/OfficeContactBookPage';
import OfficeMaterialOrdersPage from './pages/OfficeMaterialOrdersPage';
import OfficePayrollPage from './pages/OfficePayrollPage';
import OfficeSopsPage from './pages/OfficeSopsPage';
import MessagingInboxPage from './pages/MessagingInboxPage';
import WhatsappHubPage from './pages/WhatsappHubPage';
import CampaignsPage from './pages/CampaignsPage';
import MetaLeadsPage from './pages/MetaLeadsPage';
import SeoManagementPage from './pages/SeoManagementPage';
import WebsiteCmsPage from './pages/WebsiteCmsPage';
import FrontDeskPage from './pages/FrontDeskPage';
import BrokerProfilePage from './pages/BrokerProfilePage';
import { BuyerFormLinkPage, BuyerFormOfficePage } from './pages/BuyerFormPage';
import TodayPage from './pages/TodayPage';

function App() {
  const { currentUser } = useAuth();

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/broker-profile/:token" element={<BrokerProfilePage />} />
        <Route path="/buyer-form/:token" element={<BuyerFormLinkPage />} />
        <Route
          path="/login"
          element={currentUser ? <Navigate to="/" /> : <LoginPage />}
        />
        <Route
          path="/*"
          element={currentUser ? <ProtectedRoutes /> : <Navigate to="/login" />}
        />
      </Routes>
    </BrowserRouter>
  );
}

// A component to hold all the routes that require authentication
function ProtectedRoutes() {
  const { loading, roleId, viewingAs } = useAuth();
  const { canPage } = usePermissions();
  const isAdmin = String(roleId || '') === 'super-admin' || String(roleId || '') === 'admin';
  const isSalesManager = String(roleId || '') === 'sales-manager';

  // Front desk sees only the check-in screen: no CRM menu, no lead data.
  if (!loading && String(roleId || '') === 'front-desk' && !viewingAs) {
    return (
      <Routes>
        <Route path="/front-desk" element={<FrontDeskPage />} />
        <Route path="*" element={<Navigate to="/front-desk" replace />} />
      </Routes>
    );
  }

  // Managers and admins always; anyone else when given the page in User management.
  const RequireManagerOrPage = ({ pageKey, children }: { pageKey: string; children: React.ReactElement }) => {
    if (loading) return null;
    if (!isAdmin && !isSalesManager && !canPage(pageKey)) return <Navigate to="/" replace />;
    return children;
  };

  const defaultAuthedRoute = () => {
    if (viewingAs && canPage('today')) return '/today';
    if (viewingAs && canPage('frontDesk')) return '/front-desk';
    if (canPage('followUps')) return '/follow-ups';
    if (canPage('dashboard')) return '/dashboard';
    if (canPage('leads')) return '/leads';
    if (canPage('brokers')) return '/brokers';
    if (canPage('jobApplications')) return '/job-applications';
    if (canPage('reports')) return '/reports';
    if (canPage('officeMaterialOrders')) return '/office/material-orders';
    if (canPage('officePayroll')) return '/office/payroll';
    return '/login';
  };

  const RequirePage = ({
    pageKey,
    children,
  }: {
    pageKey: string;
    children: React.ReactElement;
  }) => {
    if (loading) return null;
    if (!canPage(pageKey)) return <Navigate to="/" replace />;
    return children;
  };

  const RequireSuperAdmin = ({ children }: { children: React.ReactElement }) => {
    if (loading) return null;
    if (!canPage('settings')) return <Navigate to="/" replace />;
    return children;
  };

  const RequireAdmin = ({ children }: { children: React.ReactElement }) => {
    if (loading) return null;
    if (!isAdmin) return <Navigate to="/" replace />;
    return children;
  };

  const RequireMetaLeadAccess = ({ children }: { children: React.ReactElement }) => {
    if (loading) return null;
    if (!isAdmin && !canPage('metaLeads')) return <Navigate to="/" replace />;
    return children;
  };

  const RequireOffice = ({ children }: { children: React.ReactElement }) => {
    if (loading) return null;
    if (!isAdmin && !canPage('office') && !canPage('officeMaterialOrders') && !canPage('officePayroll') && !canPage('officeUserManagement') && !canPage('officeSops')) return <Navigate to="/" replace />;
    return children;
  };

  return (
    <Routes>
      <Route path="/buyer-form/office/:projectId/:unitId/:bookingId" element={<RequireAdmin><BuyerFormOfficePage /></RequireAdmin>} />
      <Route
        path="/office/*"
        element={
          <RequireOffice>
            <OfficeLayout />
          </RequireOffice>
        }
      >
        <Route index element={isAdmin ? <Navigate to="/office/today" replace /> : <Navigate to={canPage('officeMaterialOrders') ? '/office/material-orders' : canPage('officePayroll') ? '/office/payroll' : '/office/sops'} replace />} />
        <Route path="settings" element={<RequireAdmin><OfficeSettingsPage /></RequireAdmin>} />
        <Route path="issues" element={<OfficeIssuesPage />} />
        <Route path="contact-book" element={<RequireAdmin><OfficeContactBookPage /></RequireAdmin>} />
        <Route path="units" element={<RequireAdmin><OfficeUnitsPage /></RequireAdmin>} />
        <Route path="units/:unitId" element={<RequireAdmin><OfficeUnitDetailPage /></RequireAdmin>} />
        <Route path="units/:unitId/classic" element={<RequireAdmin><OfficeUnitDetailClassicPage /></RequireAdmin>} />
        <Route path="estimation" element={<RequireAdmin><OfficeEstimationPage /></RequireAdmin>} />
        <Route path="feasibility" element={<RequireAdmin><OfficeFeasibilityPage /></RequireAdmin>} />
        <Route path="banks" element={<RequireAdmin><OfficeBanksPage /></RequireAdmin>} />
        <Route path="today" element={<RequireAdmin><OfficeTodayPage /></RequireAdmin>} />
        <Route path="reports" element={<RequireAdmin><OfficeReportsPage /></RequireAdmin>} />
        <Route path="material-orders" element={isAdmin ? <OfficeMaterialOrdersPage /> : <RequirePage pageKey="officeMaterialOrders"><OfficeMaterialOrdersPage /></RequirePage>} />
        <Route path="payroll" element={isAdmin ? <OfficePayrollPage /> : <RequirePage pageKey="officePayroll"><OfficePayrollPage /></RequirePage>} />
        <Route path="sops" element={isAdmin ? <OfficeSopsPage /> : <RequirePage pageKey="officeSops"><OfficeSopsPage /></RequirePage>} />
        <Route path="user-management" element={<RequireAdmin><Navigate to="/office/settings?section=officeUsers" replace /></RequireAdmin>} />
        <Route
          path="messages"
          element={
            <RequirePage pageKey="messages">
              <MessagingInboxPage />
            </RequirePage>
          }
        />
        <Route path="messages/settings" element={<Navigate to="/whatsapp?tab=settings" replace />} />
        <Route path="change-orders" element={<RequireAdmin><OfficeChangeOrdersPage /></RequireAdmin>} />
        <Route path="reconciliation" element={<RequireAdmin><OfficeBankReconciliationPage /></RequireAdmin>} />
        <Route path="request-forms" element={<Navigate to="/office" replace />} />
        <Route path="*" element={<Navigate to="/office" replace />} />
      </Route>

      <Route
        path="/*"
        element={
          <DashboardLayout />
        }
      >
        <Route
          index
          element={<Navigate to={defaultAuthedRoute()} replace />}
        />
        <Route
          path="dashboard"
          element={
            <RequirePage pageKey="dashboard">
              <DashboardHomePage />
            </RequirePage>
          }
        />
        <Route
          path="leads"
          element={
            <RequirePage pageKey="leads">
              <AllLeadsPage />
            </RequirePage>
          }
        />
        <Route
          path="follow-ups"
          element={
            <RequirePage pageKey="followUps">
              <FollowUpPage />
            </RequirePage>
          }
        />
        <Route
          path="brokers"
          element={
            <RequirePage pageKey="brokers">
              <BrokersPage />
            </RequirePage>
          }
        />
        <Route
          path="job-applications"
          element={
            <RequirePage pageKey="jobApplications">
              <JobApplicationsPage />
            </RequirePage>
          }
        />
        <Route
          path="reports"
          element={
            <RequirePage pageKey="reports">
              <ReportsPage />
            </RequirePage>
          }
        />
        <Route
          path="settings"
          element={
            <RequireSuperAdmin>
              <SettingsPage />
            </RequireSuperAdmin>
          }
        />
        <Route
          path="messages"
          element={
            <RequirePage pageKey="messages">
              <MessagingInboxPage />
            </RequirePage>
          }
        />
        <Route path="messages/settings" element={<Navigate to="/whatsapp?tab=settings" replace />} />
        <Route path="whatsapp" element={<RequireAdmin><WhatsappHubPage /></RequireAdmin>} />
        <Route path="today" element={<RequireManagerOrPage pageKey="today"><TodayPage /></RequireManagerOrPage>} />
        <Route path="front-desk" element={<RequireManagerOrPage pageKey="frontDesk"><FrontDeskPage /></RequireManagerOrPage>} />
        <Route
          path="campaigns"
          element={
            <RequirePage pageKey="campaigns">
              <CampaignsPage />
            </RequirePage>
          }
        />
        <Route
          path="meta-leads"
          element={<RequireMetaLeadAccess><MetaLeadsPage /></RequireMetaLeadAccess>}
        />
        <Route
          path="seo"
          element={
            <RequireAdmin>
              <SeoManagementPage />
            </RequireAdmin>
          }
        />
        <Route
          path="website-cms"
          element={
            <RequireAdmin>
              <WebsiteCmsPage />
            </RequireAdmin>
          }
        />
        <Route path="*" element={<Navigate to="/" />} />
      </Route>
    </Routes>
  );
}

export default App;
